import React from 'react';
import { X, Calendar, Building } from 'lucide-react';

interface NoteDetailsProps {
  note: {
    id: string;
    title: string;
    content: string;
    category: 'personal' | 'meeting';
    reminder_at: string | null;
    reminder_status: 'pending' | 'sent' | 'cancelled' | null;
    created_at: string;
    agency_name?: string;
  };
  onClose: () => void;
}

export default function NoteDetails({ note, onClose }: NoteDetailsProps) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {note.title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                note.category === 'meeting' 
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
              }`}>
                {note.category === 'meeting' ? 'Meeting Note' : 'Personal Note'}
              </span>
              
              {note.reminder_at && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  note.reminder_status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
                    : note.reminder_status === 'sent'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  <Calendar className="h-3 w-3 mr-1" />
                  {new Date(note.reminder_at).toLocaleString()}
                </span>
              )}
            </div>

            {note.category === 'meeting' && note.agency_name && (
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Building className="h-4 w-4 mr-2" />
                <span>{note.agency_name}</span>
              </div>
            )}

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mt-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap">{note.content}</div>
              </div>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              Created: {new Date(note.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}